import { useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { useFormContext } from "react-hook-form";
import { Form } from "@/react/components/shared/uikit/Form";
import { DatePicker } from "@circle-react/components/QuickPost/ModalForm/SubmitButton/DatePicker";
import { ONE_DAY_FROM_NOW } from "@circle-react/helpers/dateTimeHelpers";
import {
  dateFormats,
  formattedDateTime,
} from "@circle-react/helpers/dateTimeHelpers/formattedDateTime";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";
import { useModalData } from "@circle-react-shared/uikit/ModalV2";

interface DateToFinishDatePickerProps {
  formClassName: string;
  name: string;
  translationRoot: string;
}

export const DateToFinishDatePicker = ({
  name,
  formClassName,
  translationRoot,
}: DateToFinishDatePickerProps) => {
  const { watch, setValue } = useFormContext();
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const { contentPortalElement } = useModalData();

  const date = watch<string>(name);
  const formattedDate = useMemo(() => {
    if (!date) {
      return "";
    }

    return formattedDateTime({
      dateTime: new Date(date),
      format: dateFormats.long_date_short_time,
    });
  }, [date]);

  const onCancelDatePicker = (event: MouseEvent<HTMLButtonElement>) => {
    event && event.preventDefault();
    setIsDatePickerVisible(false);
  };

  const onSave = (date: string) => {
    setIsDatePickerVisible(false);
    setValue(name, date, { shouldDirty: true });
  };

  return (
    <Form.Item
      className={formClassName}
      name={name}
      translationRoot={translationRoot}
      hideLabel
      hidePlaceholder
      hideBorder
      hideDescription
      variant="extra-bottom-margin"
    >
      <div
        // eslint-disable-next-line tailwindcss/no-custom-classname -- we need form-control class
        className={classNames(
          "text-default form-control flex w-full rounded-lg",
        )}
        data-testid="scheduled_at"
      >
        <button
          className="flex w-full items-center justify-between"
          type="button"
          onClick={() => setIsDatePickerVisible(true)}
        >
          <span>{formattedDate}</span>
          <Icon type="16-calendar" size={16} className="!h-4 !w-4" />
        </button>
      </div>
      <DatePicker
        datePickerVisible={isDatePickerVisible}
        onCancel={onCancelDatePicker}
        value={date ?? ONE_DAY_FROM_NOW.toISOString()}
        onSave={onSave}
        saveButtonVariant="circle"
        placement="bottom-start"
        appendTo={contentPortalElement || "parent"}
      />
    </Form.Item>
  );
};
